const getSchema = rootURL => {
  const classicsURL = `${rootURL}/s/art/the_classics`;
  return [
    {
      '@type': 'CollectionPage',
      '@id': classicsURL,
      name: 'Jerusalem & Kotel Art',
      description:
        'Jerusalem and Kotel wall art available for purchase — from classic Western Wall scenes to modern and abstract interpretations. Prints and paintings, framed or unframed.',
      url: classicsURL,
      isPartOf: { '@id': `${rootURL}/#website` },
      about: [
        { '@type': 'Thing', name: 'Jerusalem Art' },
        { '@type': 'Thing', name: 'Kotel Art' },
        { '@type': 'Thing', name: 'Jewish Wall Art' },
      ],
      publisher: { '@id': `${rootURL}/#organization` },
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${classicsURL}#breadcrumb`,
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: `${rootURL}`,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Art',
          item: `${rootURL}/s/art`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: 'Jerusalem & Kotel Art',
          item: classicsURL,
        },
      ],
    },
    {
      '@type': 'FAQPage',
      '@id': `${classicsURL}#faq`,
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What styles of Jerusalem art are available?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'The collection includes traditional Kotel and Old City scenes, Jerusalem landscape paintings, and more modern or abstract interpretations. Some pieces are devotional in feel, while others use Jerusalem more loosely as inspiration.',
          },
        },
        {
          '@type': 'Question',
          name: 'How do I choose between a classic scene and a more modern piece?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: "It usually comes down to the room and the feeling you want. Classic Kotel scenes and Old City landscapes anchor a space with something recognizable and timeless. Modern or abstract pieces are more flexible and work across a wider range of interiors. If you're unsure, look at what's already on your walls and buy toward that tone.",
          },
        },
        {
          '@type': 'Question',
          name: 'Are these paintings or prints?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Both. The collection includes original paintings, fine art prints, and mixed media pieces. Prints are the most accessible option — clean, sharp, and available in multiple sizes. Original paintings are one-of-a-kind. Many listings offer both framed and unframed options.',
          },
        },
        {
          '@type': 'Question',
          name: 'Are these specifically religious, or suitable for any home?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Both exist here. Kotel scenes and Old City imagery carry clear religious and cultural meaning. Other pieces — landscapes, abstracts, modern interpretations — are grounded more in place than symbolism. The right choice depends on what you want the piece to convey.',
          },
        },
        {
          '@type': 'Question',
          name: 'How do I choose the right size?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'As a rough guide, most living room walls comfortably hold a piece around 60–90 cm wide. Larger walls can go bigger, especially with more minimal or abstract works.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can I commission a specific Jerusalem scene or view?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: "Yes. If you have a specific subject, style, or perspective in mind, you can submit a request through Joodaic's commissions hub.",
          },
        },
        {
          '@type': 'Question',
          name: 'Are these suitable as gifts?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. Jerusalem art is often chosen for housewarmings, bar mitzvahs, and other meaningful occasions — especially when the recipient has a personal connection to the place.',
          },
        },
      ],
    },
  ];
};

const theClassicsSeoConfig = {
  key: 'the_classics',
  match: ({ listingType, activeCollection }) =>
    listingType === 'art' && activeCollection === 'the_classics',
  accentColor: '--colorArt',
  links: [],
  title: 'Jerusalem & Kotel Art | Wall Art, Prints & Paintings | Joodaic',
  description:
    'Jerusalem and Kotel wall art available for purchase — from classic Western Wall scenes to modern and abstract interpretations. Prints and paintings, framed or unframed.',
  getSchema,
  intro: {
    heading: 'Jerusalem & Kotel Art',
    tagline:
      'Jerusalem and Kotel wall art — from classic Old City scenes to modern and abstract interpretations — chosen for homes, not catalogs.',
    collapsedHeading: 'Jerusalem & Kotel Art',
    bodyParagraphs: [],
  },
  bottomSections: [
    {
      id: 'positioning',
      type: 'prose',
      heading: 'Jerusalem art for the wall',
      paragraphs: [
        'Jerusalem is one of the most painted cities in the world. That also means it\'s one of the most reproduced — and the difference between a piece that becomes part of your home and one that disappears into the wall isn\'t always obvious.',
        'The works here range from classic Kotel scenes and Old City landscapes to more modern interpretations — pieces that use Jerusalem as a subject rather than a document. Some are quiet and devotional. Others bring color, movement, or a more contemporary eye.',
        "What they share is that they were chosen to live on a wall — not just to fill a catalog.",
        'For more personal pieces, you can explore <a href="/s/art/gedolim">Gedolim portraits</a>.',
      ],
    },
    {
      id: 'style_guide',
      title: 'Choosing a style',
      paragraphs: [
        'Classic and devotional pieces center the Kotel, the Old City gates, and the Jerusalem skyline in a traditional register — quiet, recognizable, timeless. They tend to work well in living rooms, dining rooms, and entranceways where the subject speaks for itself.',
        'Landscape and scenic works show Jerusalem from a wider view — the hills, the light, the texture of the city. These feel less specifically religious while staying grounded in place, and work across a broader range of spaces.',
        'Modern and abstract interpretations use Jerusalem as inspiration rather than documentation. More color, more movement, more open to context. These work well in contemporary interiors where the art anchors a room without defining it.',
        'Most homes mix one dominant direction with one supporting piece.',
      ],
    },
  ],
  faqHeading: 'FAQ',
  faq: [
    {
      question: 'What styles of Jerusalem art are available?',
      answer: [
        'The collection includes traditional Kotel and Old City scenes, Jerusalem landscape paintings, and more modern or abstract interpretations. Some pieces are devotional in feel, while others use Jerusalem more loosely as inspiration.',
      ],
    },
    {
      question: 'How do I choose between a classic scene and a more modern piece?',
      answer: [
        "It usually comes down to the room and the feeling you want. Classic Kotel scenes and Old City landscapes anchor a space with something recognizable and timeless. Modern or abstract pieces are more flexible and work across a wider range of interiors. If you're unsure, look at what's already on your walls and buy toward that tone.",
      ],
    },
    {
      question: 'Are these paintings or prints?',
      answer: [
        'Both. The collection includes original paintings, fine art prints, and mixed media pieces. Prints are the most accessible option — clean, sharp, and available in multiple sizes. Original paintings are one-of-a-kind. Many listings offer both framed and unframed options.',
      ],
    },
    {
      question: 'Are these specifically religious, or suitable for any home?',
      answer: [
        'Both exist here. Kotel scenes and Old City imagery carry clear religious and cultural meaning. Other pieces — landscapes, abstracts, modern interpretations — are grounded more in place than symbolism. The right choice depends on what you want the piece to convey.',
      ],
    },
    {
      question: 'How do I choose the right size?',
      answer: [
        'As a rough guide, most living room walls comfortably hold a piece around 60–90 cm wide. Larger walls can go bigger, especially with more minimal or abstract works.',
      ],
    },
    {
      question: 'Can I commission a specific Jerusalem scene or view?',
      answer: [
        "Yes. If you have a specific subject, style, or perspective in mind, you can submit a request through Joodaic's <a href=\"/artist/custom_co_studio\">commissions hub</a>.",
      ],
    },
    {
      question: 'Are these suitable as gifts?',
      answer: [
        'Yes. Jerusalem art is often chosen for housewarmings, bar mitzvahs, and other meaningful occasions — especially when the recipient has a personal connection to the place.',
      ],
    },
  ],
};

export default theClassicsSeoConfig;
